import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import {
  AGENT_PLATFORM_LABELS,
  AGENT_PLATFORMS,
  getAgentPlatformKey,
  getInventoryPlatformKey,
  INVENTORY_PLATFORM_LABELS,
  isAgentPlatform
} from "@/domain/ai-inventory"
import {
  DEFAULT_LONG_FORMAT,
  getDateInUserLocale,
  getRelativeTimeFromNow,
  toMs
} from "@/helpers/date.helpers"
import { RESOURCES } from "@/organisms/Form/components/ResourcesSectionForm/const"
import {
  AGENT_POLICIES_HEADING_KEY,
  ALL_AGENTS_HEADING_KEY,
  ALL_MCP_SERVERS_HEADING_KEY,
  MCP_SERVER_POLICIES_HEADING_KEY
} from "@/pages/GovernancePage/components/organisms/AiPolicyProvider/getData"

import type {
  AiMcpServerScopeOption,
  AiPoliciesResponse,
  AiPolicy,
  AiPolicyAccessType,
  AiPolicyCondition,
  AiPolicyDecision,
  AiPolicyGuardrail,
  AiPolicyInput,
  AiPolicyKind,
  AiPolicyNetworkDestination,
  AiPolicyOptionsResponse,
  AiPolicyScope
} from "@/api/useAiPolicies.types"
import type { AgentPlatformAi } from "@/domain/ai-inventory"
import type { TFunction } from "@/hooks/useTranslation"
import type { Status } from "@/molecules/StatusTag"
import type { SpecificResourcesProps } from "@/organisms/Form/components/ResourcesSectionForm"
import type {
  Record as PolicyListRecord,
  PolicyRecord,
  RuleOption
} from "@/pages/GovernancePage/applications/policies/getDemoData.types"
import type { PolicyRecordInput } from "@/pages/GovernancePage/applications/policies/useAiPolicies.types"
import type {
  AiPolicyFormDestination,
  AiPolicyFormResource,
  AiPolicyFormRule,
  AiPolicyFormRuleGroup,
  AiPolicyFormValues
} from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"
import type {
  AiDecisionOutcome,
  AiPolicyMcpServerOption,
  AiPolicyScopeResource,
  AiPolicySelectOption,
  LiveRuleOption,
  LiveRuleOptionGroup
} from "./useLiveAiPolicies.types"

export const AI_POLICY_ACCESS_TYPES: AiPolicyAccessType[] = [
  "network",
  "file_system",
  "dynamic_code_execution"
]

// The scope option holding the root agents a policy can name
export const SCOPE_AGENT_OPTION = "agentId"

// The conditions whose clause is a list of values
const LIST_CONDITIONS = [
  "toolNames",
  "toolCategories",
  "toolSources",
  "connectors"
] as const

type ListCondition = (typeof LIST_CONDITIONS)[number]

const ARGUMENTS = "arguments"

// The conditions a rule group sets alone, by their rule id
const GUARDRAILS = {
  promptInjection: "PROMPT_INJECTION",
  pii: "PII",
  secrets: "SECRETS"
} as const satisfies Record<string, AiPolicyGuardrail>

// A condition's access types and network destinations, each edited as a rule
// of its card, beside the tool names they narrow
export const ACCESS_TYPES_RULE = "accessTypes"
export const NETWORK_DESTINATION_RULE = "networkDestination"

// Every process of the agent's workload, not a tool: its card holds its
// access types and its network destinations
export const WHOLE_WORKLOAD_RULE = "wholeWorkload"
// The access types the whole workload takes for now
export const WHOLE_WORKLOAD_ACCESS_TYPES: AiPolicyAccessType[] = ["network"]

// Enforced only by the Kodem sensor, which runs on Kubernetes and AgentCore
const SENSOR_RULES = [NETWORK_DESTINATION_RULE, WHOLE_WORKLOAD_RULE]
const SENSOR_PLATFORMS: AgentPlatformAi[] = ["AGENTCORE"]

// Only an agent platform enforces them: it sees the call
const PLATFORM_RULES = [...Object.keys(GUARDRAILS), "connectors", ARGUMENTS]

// The Kodem sensor's conditions and an agent platform's cannot share a rule:
// the sensor enforces in-host, and only a platform checks its own, at the call
export const hasSensorAndPlatformRules = (conditions: (string | undefined)[]) =>
  conditions.some((condition) => SENSOR_RULES.includes(condition ?? "")) &&
  conditions.some((condition) => PLATFORM_RULES.includes(condition ?? ""))

const GUARDRAIL_PLATFORMS: AgentPlatformAi[] = ["AGENTCORE", "COPILOT_STUDIO"]

const isKeyOf = <T extends object>(
  map: T,
  key: string | undefined
): key is Extract<keyof T, string> => !!key && key in map

const findKey = (map: Record<string, string>, value: string | undefined) =>
  Object.keys(map).find((key) => map[key] === value)

const getSoleRuleId = (condition: AiPolicyCondition) =>
  findKey(GUARDRAILS, condition.guardrail)

const getSoleCondition = (
  rules: AiPolicyFormRule[]
): AiPolicyCondition | undefined => {
  for (const { condition } of rules) {
    if (isKeyOf(GUARDRAILS, condition))
      return { guardrail: GUARDRAILS[condition] }
  }
}

const AGENT: AiPolicyScopeResource = "agent"
const PLATFORM: AiPolicyScopeResource = "platform"
const MCP_SERVER: AiPolicyScopeResource = "mcpServer"

export const AI_POLICY_KINDS: AiPolicyKind[] = ["agent", "mcpServer"]

// The query string key the creation page takes the new policy's kind from
export const AI_POLICY_KIND_PARAM = "kind"

const isPolicyKind = (
  value: string | null | undefined
): value is AiPolicyKind => AI_POLICY_KINDS.some((kind) => kind === value)

// A policy's kind: one without is an agent policy, as the API takes it. A new
// policy has the kind its creation page was opened for
export const getAiPolicyKind = (
  policy: Pick<AiPolicy, "kind"> | undefined,
  param?: string | null
): AiPolicyKind => {
  const kind = policy ? policy.kind : param

  return isPolicyKind(kind) ? kind : "agent"
}

// The API takes an agent policy by default, and one that knows no kinds takes
// no kind: only an MCP server policy says its own
const getKindField = (kind: AiPolicyKind | undefined) =>
  kind === "mcpServer" ? { kind } : {}

export const getAiPolicies = (response: AiPoliciesResponse): AiPolicy[] =>
  response.data ?? []

// The options of one of the scope or condition options the API serves
export const getOptions = (
  response: AiPolicyOptionsResponse | undefined,
  id: string
): AiPolicySelectOption[] => {
  const options = response?.data?.find((option) => option.id === id)?.opts

  return (options ?? []).map((option) => {
    const platform = getAgentPlatformKey(option.platform)

    return {
      value: option.id,
      label: option.displayName,
      title: option.description,
      ...(platform ? { platform } : {})
    }
  })
}

// The agents the scope's picker offers: with platforms chosen, only the agents
// on them, and the ones already named, so they still read by name
export const getScopeAgentOptions = (
  scope: AiPolicyScope,
  agents: AiPolicySelectOption[]
) => {
  const platforms = scope.platforms ?? []

  if (!platforms.length) return agents

  const ids = new Set(getScopeAgentIds(scope))

  return agents.filter(
    (agent) =>
      ids.has(agent.value) ||
      (!!agent.platform && platforms.includes(agent.platform))
  )
}

// The agents the scope names that run on none of its platforms: the policy
// would never reach them. An agent no platform hosts is not known to run off them
export const getAgentsOffScopePlatforms = (
  scope: AiPolicyScope,
  agents: AiPolicySelectOption[]
) => {
  const platforms = scope.platforms ?? []
  const ids = new Set(getScopeAgentIds(scope))

  return agents.filter(
    ({ value, platform }) =>
      ids.has(value) &&
      !!platform &&
      platforms.length > 0 &&
      !platforms.includes(platform)
  )
}

// The AgentCore agents the scope names: their own policies apply only to
// calls Kodem can trace to them
export const getAgentCoreScopeAgents = (
  scope: AiPolicyScope,
  agents: AiPolicySelectOption[]
) => {
  const ids = new Set(getScopeAgentIds(scope))

  return agents.filter(
    (agent) => ids.has(agent.value) && agent.platform === "AGENTCORE"
  )
}

// A decision can come from a Kubernetes cluster, which a policy cannot be
// scoped to: its platform is an inventory platform
const getPlatformLabel = (platform: string | null | undefined) => {
  const key = getInventoryPlatformKey(platform ?? undefined)
  if (key) return INVENTORY_PLATFORM_LABELS[key]

  return platform || NO_VALUE_PLACEHOLDER
}

const getPlatformsLabel = (scope: AiPolicyScope | undefined, t: TFunction) =>
  scope?.platforms?.length
    ? scope.platforms.map(getPlatformLabel).join(", ")
    : t("governance.ai.live.scope.anyPlatform")

const getDateLabels = (date: number | string | undefined) => {
  const ms = toMs(date)

  return ms
    ? {
        text: getRelativeTimeFromNow(ms),
        tooltip: getDateInUserLocale(ms, DEFAULT_LONG_FORMAT)
      }
    : { text: NO_VALUE_PLACEHOLDER }
}

const getScopeAgentIds = (scope: AiPolicyScope | undefined) => {
  if (scope?.agentIds?.length) return scope.agentIds

  return scope?.agentId ? [scope.agentId] : []
}

// The conditions a deny rule can set, in the rule cards' option groups
export const getLiveRuleOptions = (
  t: TFunction,
  options: {
    toolCategories: AiPolicySelectOption[]
    toolSources: AiPolicySelectOption[]
    connectors?: AiPolicySelectOption[]
  }
): LiveRuleOptionGroup[] => {
  const tKey = "governance.ai.form.rules"
  const option = (
    id: ListCondition | typeof ARGUMENTS | keyof typeof GUARDRAILS,
    scope: RuleOption["scope"],
    rest: {
      input?: RuleOption["input"]
      detection?: LiveRuleOption["detection"]
      options?: AiPolicySelectOption[]
    }
  ) => ({
    id,
    type: "single" as const,
    displayName: t(`${tKey}.${id}`),
    scope,
    input: rest.input,
    detection: rest.detection,
    options: (rest.options ?? []).map(({ label, value }) => ({ label, value }))
  })

  return [
    {
      displayName: t("governance.ai.live.ruleGroups.tool"),
      title: "tool",
      options: [
        option("toolNames", "matches", { input: "tags" }),
        option("toolCategories", "only", { options: options.toolCategories }),
        option("toolSources", "only", { options: options.toolSources })
      ]
    },
    {
      displayName: t("governance.ai.live.ruleGroups.call"),
      title: "call",
      options: [
        option("connectors", "only", {
          input: "tags",
          options: options.connectors
        }),
        option(ARGUMENTS, undefined, { input: "argument" })
      ]
    },
    {
      displayName: t("governance.ai.live.ruleGroups.guardrails"),
      title: "guardrails",
      options: [
        option("promptInjection", undefined, { detection: "detected" }),
        option("pii", undefined, { detection: "detected" }),
        option("secrets", undefined, { detection: "areDetected" })
      ]
    }
  ]
}

// What the specific scope of a policy can name: its agents and platforms
export const getScopeOptions = (
  t: TFunction,
  agents: AiPolicySelectOption[]
): NonNullable<SpecificResourcesProps["options"]> => {
  const tKey = "governance.ai.live.form.resources"

  return [
    {
      label: t(`${tKey}.agent.label`),
      value: AGENT,
      placeholder: t(`${tKey}.agent.placeholder`),
      valueType: "SELECT_ARRAY",
      opts: agents.map(({ value, label }) => ({
        id: value,
        displayName: label
      }))
    },
    {
      label: t(`${tKey}.platform.label`),
      value: PLATFORM,
      placeholder: t(`${tKey}.platform.placeholder`),
      valueType: "SELECT_ARRAY",
      opts: AGENT_PLATFORMS.map((platform) => ({
        id: platform,
        displayName: AGENT_PLATFORM_LABELS[platform]
      }))
    }
  ]
}

const MCP_RESOURCES = "governance.ai.live.form.mcpResources"

// Why an MCP server can, or cannot, be picked: a remote one says how many of
// its tools the Kodem sensor can block, and in which images
const getMcpServerNote = (t: TFunction, server: AiMcpServerScopeOption) => {
  const tKey = `${MCP_RESOURCES}.kinds`

  switch (server.kind) {
    case "remote":
      return [
        t(`${tKey}.remote`, {
          enforceable: server.enforceableTools ?? 0,
          tools: server.tools ?? 0
        }),
        ...(server.images ?? [])
      ].join(" · ")
    case "local":
      return t(`${tKey}.local`, {
        agents: (server.agents ?? [])
          .map((agent) => agent.name || agent.id)
          .join(", ")
      })
    case "saas":
      return t(`${tKey}.saas`)
    case "notSeenRunning":
      return t(`${tKey}.notSeenRunning`)
  }
}

// The MCP servers the scope's picker offers: only a remote one can be picked
export const getMcpServerScopeOptions = (
  t: TFunction,
  servers: AiMcpServerScopeOption[]
): AiPolicyMcpServerOption[] =>
  servers.map((server) => ({
    value: server.name,
    label: server.name,
    description: getMcpServerNote(t, server),
    disabled: server.kind !== "remote"
  }))

// Why the Kodem sensor cannot block a tool: a local server's tools are its
// agents', which their own policies cover
type McpTool = { enforceable: boolean; reason?: string }

// The tools of the servers an MCP server policy's scope names, or of every
// remote server for all of them. A tool several servers have is blockable if
// any can block it
const getMcpTools = (
  servers: AiMcpServerScopeOption[],
  scope: AiPolicyScope
) => {
  const names = new Set(scope.mcpServerNames ?? [])
  const tools = new Map<string, McpTool>()

  servers
    .filter((server) =>
      scope.allMcpServers ? server.kind === "remote" : names.has(server.name)
    )
    .flatMap((server) =>
      (server.serverTools ?? []).map((tool) => ({
        ...tool,
        reason:
          server.kind === "local" ? "localServer" : tool.unenforceableReason
      }))
    )
    .forEach(({ name, enforceable, reason }) => {
      const known = tools.get(name)

      if (known?.enforceable) return
      tools.set(
        name,
        enforceable
          ? { enforceable }
          : { enforceable, reason: known?.reason ?? reason }
      )
    })

  return tools
}

const getMcpToolReason = (t: TFunction, reason: string | undefined) =>
  t(`${MCP_RESOURCES}.toolReasons.${reason ?? "unknown"}`, {
    defaultValue: t(`${MCP_RESOURCES}.toolReasons.unknown`)
  })

// The tools a Tool Access rule of an MCP server policy can name: the blockable
// first, then the others, each marked that it can't be blocked and why. None
// is disabled: a picked one stays removable
export const getMcpToolOptions = (
  t: TFunction,
  servers: AiMcpServerScopeOption[],
  scope: AiPolicyScope
): RuleOption["options"] =>
  [...getMcpTools(servers, scope)]
    .sort(
      ([a, left], [b, right]) =>
        Number(right.enforceable) - Number(left.enforceable) ||
        a.localeCompare(b)
    )
    .map(([name, { enforceable, reason }]) => ({
      label: name,
      value: name,
      ...(!enforceable && {
        description: t(`${MCP_RESOURCES}.cantBlock`, {
          reason: getMcpToolReason(t, reason)
        })
      })
    }))

// A tool name is a case-insensitive glob: * is the only wildcard
const matchesToolName = (pattern: string, name: string) =>
  new RegExp(
    `^${pattern
      .split("*")
      .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
      .join(".*")}$`,
    "i"
  ).test(name)

// Why the Kodem sensor may block none of a Tool Access rule's tool names on
// the servers the scope names: a name matching no tool of theirs, or only
// tools it cannot block. A warning, not an error: the servers can change
export const getMcpToolWarning = (
  t: TFunction,
  toolNames: string[],
  servers: AiMcpServerScopeOption[],
  scope: AiPolicyScope
) => {
  const tools = [...getMcpTools(servers, scope)]

  for (const toolName of toolNames) {
    const matched = tools.filter(([name]) => matchesToolName(toolName, name))

    if (!matched.length)
      return t(`${MCP_RESOURCES}.toolWarnings.notFound`, { name: toolName })
    if (!matched.some(([, tool]) => tool.enforceable))
      return t(`${MCP_RESOURCES}.toolWarnings.cantBlock`, {
        name: toolName,
        reason: getMcpToolReason(t, matched[0][1].reason)
      })
  }
}

// What is wrong with the MCP servers a scope names: one newly named that is
// not remote, as the API refuses it, or one no scan holds. Those the policy
// was saved with are kept: their deployment can have changed since
export const getMcpScopeError = (
  t: TFunction,
  names: string[],
  servers: AiMcpServerScopeOption[],
  saved: string[] = []
) => {
  const byName = new Map(servers.map((server) => [server.name, server]))

  for (const name of names) {
    const server = byName.get(name)

    if (saved.includes(name)) continue
    if (!server) return t(`${MCP_RESOURCES}.errors.notFound`, { name })
    if (server.kind !== "remote")
      return t(`${MCP_RESOURCES}.errors.notRemote`, {
        name,
        reason: getMcpServerNote(t, server)
      })
  }
}

const getRuleGroup = (
  policy: AiPolicy,
  condition: AiPolicyCondition,
  t: TFunction
): PolicyRecord => {
  const args = Object.entries(condition.arguments ?? {})
  const soleId = getSoleRuleId(condition)

  return {
    rules: soleId
      ? [{ id: soleId, value: [] }]
      : condition.wholeWorkload
        ? [{ id: WHOLE_WORKLOAD_RULE, value: [] }]
        : [
            ...LIST_CONDITIONS.flatMap((id) =>
              condition[id]?.length ? [{ id, value: condition[id] }] : []
            ),
            ...(args.length
              ? [
                  {
                    id: ARGUMENTS,
                    value: args.map(([name, regex]) =>
                      t("governance.ai.live.argumentMatch", { name, regex })
                    )
                  }
                ]
              : [])
          ],
    actions: {
      enabled: true,
      enforceRuleOnRuntime: policy.action === "BLOCK",
      preventViolationOnDevelopment: false,
      notifyOnViolation: false,
      monitorOnViolation: policy.action === "MONITOR"
    }
  }
}

// A real policy as a row of the policies list: all-agents policies under the
// first heading, the policies of named agents under the agent heading
export const getAiPolicyRecords = (
  policies: AiPolicy[],
  agentOptions: AiPolicySelectOption[],
  t: TFunction
): PolicyListRecord[] => {
  const agentNames = new Map(agentOptions.map((o) => [o.value, o.label]))
  const toRecord = (policy: AiPolicy): PolicyRecordInput => {
    const isMcpServer = getAiPolicyKind(policy) === "mcpServer"
    const ids = isMcpServer
      ? (policy.scope?.mcpServerNames ?? [])
      : getScopeAgentIds(policy.scope)
    const common = {
      id: policy.id,
      key: policy.id,
      scopeId: policy.id,
      count: ids.length,
      total: ids.length,
      overrides: "",
      // An MCP server policy has no platforms
      description: isMcpServer ? "" : getPlatformsLabel(policy.scope, t),
      policyId: policy.id,
      policyName: policy.name,
      policyDescription: policy.description ?? "",
      violated: !!policy.violated,
      agentsDescription: "",
      allowed: [],
      notAllowed: (policy.conditions ?? []).map((condition) =>
        getRuleGroup(policy, condition, t)
      ),
      live: policy
    }

    if (isMcpServer)
      return {
        ...common,
        type: "scope",
        name: policy.scope?.allMcpServers
          ? t("governance.ai.live.scope.allMcpServers")
          : ids.join(", "),
        expandable: false,
        agents: []
      }

    if (ids.length === 1 && !policy.scope?.allAgents)
      return {
        ...common,
        type: "agent",
        name: policy.agentName || agentNames.get(ids[0]) || ids[0],
        category: "",
        grantedBy: "",
        expires: ""
      }

    return {
      ...common,
      type: "scope",
      name: policy.scope?.allAgents
        ? t("governance.ai.live.scope.allAgents")
        : ids.map((id) => agentNames.get(id) || id).join(", "),
      expandable: false,
      agents: []
    }
  }
  const records = policies.map(toRecord)
  const heading = (
    key: string,
    name: string,
    count: number,
    description?: string
  ) => ({
    id: key,
    key,
    scopeId: key,
    name,
    type: "heading" as const,
    count,
    total: count,
    overrides: "",
    ...(description
      ? {
          description: t("governance.ai.live.summary.platforms", {
            platforms: description
          })
        }
      : {})
  })
  // A scope description all the group's policies share shows once, on its
  // heading
  const group = (key: string, name: string, items: PolicyRecordInput[]) => {
    if (!items.length) return []

    const descriptions = new Set(items.map((item) => item.description))
    const shared =
      descriptions.size === 1 ? items[0].description || undefined : undefined

    return [
      heading(key, name, items.length, shared),
      ...(shared
        ? items.map((item) => ({ ...item, groupedDescription: true }))
        : items)
    ]
  }
  const byKind = (kind: AiPolicyKind) =>
    records.filter((record) => getAiPolicyKind(record.live) === kind)
  const agents = byKind("agent")
  const mcpServers = byKind("mcpServer")
  const isAllAgents = (record: PolicyRecordInput) =>
    !!record.live?.scope?.allAgents
  const isAllMcpServers = (record: PolicyRecordInput) =>
    !!record.live?.scope?.allMcpServers

  // The headings keep this order, whatever the policies' own order: within
  // each, the policies keep theirs
  return [
    ...group(
      ALL_AGENTS_HEADING_KEY,
      t("governance.ai.live.headings.allAgents"),
      agents.filter(isAllAgents)
    ),
    ...group(
      AGENT_POLICIES_HEADING_KEY,
      t("governance.ai.live.headings.agents"),
      agents.filter((record) => !isAllAgents(record))
    ),
    ...group(
      ALL_MCP_SERVERS_HEADING_KEY,
      t("governance.ai.live.headings.allMcpServers"),
      mcpServers.filter(isAllMcpServers)
    ),
    ...group(
      MCP_SERVER_POLICIES_HEADING_KEY,
      t("governance.ai.live.headings.mcpServers"),
      mcpServers.filter((record) => !isAllMcpServers(record))
    )
  ]
}

export const getAiPolicyStatus = (
  policy: AiPolicy
): { status: Status; key: string } => {
  if (!policy.enabled) return { status: "neutral", key: "disabled" }
  if (!policy.status || policy.status === "active")
    return { status: "success", key: "active" }

  return { status: "warning", key: policy.status }
}

const toFormRules = (condition: AiPolicyCondition): AiPolicyFormRule[] => {
  const soleId = getSoleRuleId(condition)

  // A sole condition takes no other: no empty rule to add one
  if (soleId) return [{ condition: soleId }]
  // The whole workload takes its network access type, saved or not, and its
  // destinations; a row to add them while they are missing
  if (condition.wholeWorkload)
    return [
      { condition: WHOLE_WORKLOAD_RULE },
      ...getAccessRules({
        accessTypes: WHOLE_WORKLOAD_ACCESS_TYPES,
        networkDestinations: condition.networkDestinations
      }),
      ...(condition.networkDestinations?.length ? [] : [{}])
    ]

  // Access types or destinations make it a Tool Access card
  const toolAccess =
    !!condition.accessTypes?.length || !!condition.networkDestinations?.length

  return [
    ...LIST_CONDITIONS.flatMap((id) =>
      condition[id]?.length
        ? [
            {
              condition: id,
              value: condition[id],
              ...(id === "toolNames" && toolAccess && { toolAccess })
            }
          ]
        : []
    ),
    ...Object.entries(condition.arguments ?? {}).map(([name, regex]) => ({
      condition: ARGUMENTS,
      name,
      regex
    })),
    ...getAccessRules(condition),
    {}
  ]
}

// An MCP server policy's scope is a list of servers, or every server
const getMcpFormResources = (scope: AiPolicyScope | undefined) => {
  const names = scope?.mcpServerNames ?? []

  return names.length
    ? {
        resources: RESOURCES.SPECIFIC,
        specific_resources: [{ type: MCP_SERVER, value: names }]
      }
    : { resources: RESOURCES.ALL, specific_resources: [{ type: MCP_SERVER }] }
}

const getFormResources = (
  scope: AiPolicyScope | undefined,
  kind: AiPolicyKind
) => {
  if (kind === "mcpServer") return getMcpFormResources(scope)

  const ids = getScopeAgentIds(scope)
  const platforms = scope?.platforms ?? []
  const resources: AiPolicyFormResource[] = [
    ...(ids.length && !scope?.allAgents ? [{ type: AGENT, value: ids }] : []),
    ...(platforms.length ? [{ type: PLATFORM, value: platforms }] : [])
  ]

  return resources.length
    ? { resources: RESOURCES.SPECIFIC, specific_resources: resources }
    : { resources: RESOURCES.ALL, specific_resources: [{}] }
}

// The form fields of a real policy; a new one monitors every agent, or every
// MCP server
export const getAiPolicyFormValues = (
  policy: AiPolicy | undefined,
  kind: AiPolicyKind = getAiPolicyKind(policy)
): Partial<AiPolicyFormValues> => {
  if (!policy) return { ...getFormResources(undefined, kind), enforce: false }

  return {
    name_details: policy.name,
    description_details: policy.description ?? "",
    ...getFormResources(policy.scope, kind),
    notAllow: (policy.conditions ?? []).map((condition) => ({
      rules: toFormRules(condition)
    })),
    enforce: policy.action === "BLOCK"
  }
}

const trimmed = (value: string | undefined) => value?.trim() || undefined

const isValue = (value: string | undefined): value is string => !!value

const values = (list: (string | undefined)[]) => {
  const result = [...new Set(list.map(trimmed).filter(isValue))]

  return result.length ? result : undefined
}

// The clauses a rule group sets; a group without any matches nothing
export const getCondition = (
  group: AiPolicyFormRuleGroup | undefined
): AiPolicyCondition | undefined => {
  // The form adds a new rule row as undefined
  const rules = (group?.rules ?? []).filter(Boolean)
  const sole = getSoleCondition(rules)

  if (sole) return sole
  // The whole workload takes network access to its destinations only
  if (rules.some((rule) => rule.condition === WHOLE_WORKLOAD_RULE))
    return {
      wholeWorkload: true,
      accessTypes: [...WHOLE_WORKLOAD_ACCESS_TYPES],
      ...getAccess(
        rules.filter((rule) => rule.condition === NETWORK_DESTINATION_RULE)
      )
    }

  const args = rules.flatMap((rule): [string, string][] => {
    const name = trimmed(rule.name)
    const regex = trimmed(rule.regex)

    return rule.condition === ARGUMENTS && name && regex ? [[name, regex]] : []
  })
  const clauses: AiPolicyCondition = {}

  LIST_CONDITIONS.forEach((id) => {
    const list = values(
      rules
        .filter((rule) => rule.condition === id)
        .flatMap((rule) => [rule.value ?? []].flat())
    )

    if (list) clauses[id] = list
  })

  if (args.length) clauses.arguments = Object.fromEntries(args)

  return Object.keys(clauses).length
    ? { ...clauses, ...getAccess(rules) }
    : undefined
}

const getResourceValues = (
  values: Pick<AiPolicyFormValues, "specific_resources">,
  type: AiPolicyScopeResource
) =>
  (values.specific_resources ?? [])
    .filter((resource) => resource?.type === type)
    .flatMap((resource) => [resource.value ?? []].flat())

// One agent is the single-agent scope, several a list, none every agent
const getScopeAgents = (ids: string[]): AiPolicyScope => {
  if (ids.length === 0) return { allAgents: true }

  return ids.length === 1 ? { agentId: ids[0] } : { agentIds: ids }
}

// The MCP servers the scope names, or every one
// Specific servers stay specific, even none, which the API refuses: never
// every server in their place
const getMcpScope = (
  values: Pick<AiPolicyFormValues, "resources" | "specific_resources">
): AiPolicyScope =>
  values.resources === RESOURCES.SPECIFIC
    ? { mcpServerNames: [...new Set(getResourceValues(values, MCP_SERVER))] }
    : { allMcpServers: true }

export const getAiPolicyScope = (
  values: Pick<AiPolicyFormValues, "resources" | "specific_resources">,
  kind: AiPolicyKind = "agent"
): AiPolicyScope => {
  if (kind === "mcpServer") return getMcpScope(values)
  if (values.resources !== RESOURCES.SPECIFIC) return { allAgents: true }

  const ids = [...new Set(getResourceValues(values, AGENT))]
  const platforms = [
    ...new Set(getResourceValues(values, PLATFORM).filter(isAgentPlatform))
  ]
  const agents = getScopeAgents(ids)

  return platforms.length ? { ...agents, platforms } : agents
}

const getConditions = (values: Pick<AiPolicyFormValues, "notAllow">) =>
  (values.notAllow ?? [])
    .map(getCondition)
    .filter((condition): condition is AiPolicyCondition => !!condition)

export const getAiPolicyRequest = (
  values: AiPolicyFormValues,
  enabled: boolean,
  kind: AiPolicyKind = "agent"
): AiPolicyInput => ({
  ...getKindField(kind),
  name: values.name_details.trim(),
  ...(trimmed(values.description_details)
    ? { description: trimmed(values.description_details) }
    : {}),
  enabled,
  action: values.enforce ? "BLOCK" : "MONITOR",
  scope: getAiPolicyScope(values, kind),
  conditions: getConditions(values)
})

// Connectors are Copilot Studio's: a scope on other platforms only has none
export const hasConnectors = (platforms: AgentPlatformAi[]) =>
  platforms.length === 0 || platforms.includes("COPILOT_STUDIO")

// The translation key of why the scope's platforms cannot enforce the condition
export const getConditionPlatformError = (
  condition: string | undefined,
  platforms: AgentPlatformAi[]
) => {
  const supports = (supported: AgentPlatformAi[]) =>
    platforms.length > 0 &&
    platforms.every((platform) => supported.includes(platform))

  if (isKeyOf(GUARDRAILS, condition) && !supports(GUARDRAIL_PLATFORMS))
    return "governance.ai.live.form.rule.errors.guardrailPlatforms"
  if (
    SENSOR_RULES.includes(condition ?? "") &&
    platforms.length > 0 &&
    !platforms.some((platform) => SENSOR_PLATFORMS.includes(platform))
  )
    return "governance.ai.live.form.rule.errors.sensorPlatforms"
}

export const hasCondition = (groups: AiPolicyFormRuleGroup[] | undefined) =>
  getConditions({ notAllow: groups }).length > 0

// Only the policy's own fields: the API serves display fields too
export const toAiPolicyInput = (policy: AiPolicy): AiPolicyInput => {
  if (!policy.action) throw new Error("The policy's action is unknown")

  return {
    ...getKindField(policy.kind),
    name: policy.name,
    ...(policy.description ? { description: policy.description } : {}),
    enabled: policy.enabled,
    action: policy.action,
    scope: policy.scope,
    conditions: policy.conditions
  }
}

export const isRegex = (value: string | undefined) => {
  try {
    new RegExp(value || "")

    return true
  } catch {
    return false
  }
}

const MAX_PORT = 65535

// A port, or an inclusive range as 8000-8080; empty is any port, which sets
// none. Undefined when it is neither
export const getPortRange = (
  ports: string | undefined
): Pick<AiPolicyNetworkDestination, "portFrom" | "portTo"> | undefined => {
  const text = ports?.trim()

  if (!text) return {}

  const match = /^(\d+)\s*(?:-\s*(\d+))?$/.exec(text)

  if (!match) return undefined

  const portFrom = Number(match[1])
  const portTo = Number(match[2] ?? match[1])

  if (portFrom < 1 || portFrom > portTo || portTo > MAX_PORT) return undefined

  return { portFrom, portTo }
}

const IPV4_OCTET = "(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)"
const IPV4 = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}$`)
const HEXTET = /^[\da-f]{1,4}$/i
const IPV6_HEXTETS = 8
const PREFIX_LENGTH = /^(0|[1-9]\d{0,2})$/

const parseIpv4 = (address: string) =>
  IPV4.test(address) ? address.split(".").map(Number) : undefined

// The hextets of one side of an IPv6 address's ::. The last side may end in
// an IPv4 address, its last two hextets
const parseHextets = (side: string, isLast: boolean) => {
  const groups = side ? side.split(":") : []
  const ipv4 = isLast ? parseIpv4(groups.at(-1) ?? "") : undefined

  if (ipv4)
    groups.splice(
      -1,
      1,
      (ipv4[0] * 256 + ipv4[1]).toString(16),
      (ipv4[2] * 256 + ipv4[3]).toString(16)
    )

  return groups.every((group) => HEXTET.test(group))
    ? groups.map((group) => parseInt(group, 16))
    : undefined
}

// The 16 bytes of an IPv6 address, without a zone. Its :: stands for one zero
// hextet at least
const parseIpv6 = (address: string) => {
  const sides = address.split("::")
  const isCompressed = sides.length === 2

  if (sides.length > 2) return undefined

  const head = parseHextets(sides[0], !isCompressed)
  const tail = isCompressed ? parseHextets(sides[1], true) : []

  if (!head || !tail) return undefined

  const zeros = IPV6_HEXTETS - head.length - tail.length

  if (isCompressed ? zeros < 1 : zeros !== 0) return undefined

  return [...head, ...Array<number>(zeros).fill(0), ...tail].flatMap(
    (hextet) => [Math.floor(hextet / 256), hextet % 256]
  )
}

// ::ffff:0:0/96, an IPv4 address written as IPv6
const isIpv4Mapped = (bytes: number[]) =>
  bytes.slice(0, 10).every((byte) => byte === 0) &&
  bytes[10] === 255 &&
  bytes[11] === 255

// Its canonical text: lowercase hextets, the first longest run of two zero
// hextets or more written as ::
const formatIpv6 = (bytes: number[]) => {
  const hextets = Array.from({ length: IPV6_HEXTETS }, (_, index) =>
    (bytes[index * 2] * 256 + bytes[index * 2 + 1]).toString(16)
  )
  let run = { start: 0, length: 1 }

  hextets.forEach((_, start) => {
    let length = 0

    while (hextets[start + length] === "0") length += 1
    if (length > run.length) run = { start, length }
  })

  if (run.length < 2) return hextets.join(":")

  return `${hextets.slice(0, run.start).join(":")}::${hextets
    .slice(run.start + run.length)
    .join(":")}`
}

// The address with the bits past the prefix length cleared
const getNetworkBytes = (bytes: number[], bits: number) =>
  bytes.map((byte, index) => {
    const kept = Math.min(Math.max(bits - index * 8, 0), 8)

    return byte - (byte % 2 ** (8 - kept))
  })

// An IPv4 or IPv6 address, alone or with a prefix length. Undefined when it
// is neither; with host bits set, the block it likely means
const parseCidr = (value: string): { network?: string } | undefined => {
  const [address = "", bits, ...rest] = value.split("/")
  const ipv4 = parseIpv4(address)
  const bytes = ipv4 ?? parseIpv6(address)

  if (rest.length || !bytes || (!ipv4 && isIpv4Mapped(bytes))) return undefined
  if (bits === undefined) return {}
  if (!PREFIX_LENGTH.test(bits) || Number(bits) > bytes.length * 8)
    return undefined

  const network = getNetworkBytes(bytes, Number(bits))

  if (network.every((byte, index) => byte === bytes[index])) return {}

  return {
    network: `${ipv4 ? network.join(".") : formatIpv6(network)}/${bits}`
  }
}

const HOSTNAME_LABEL = "[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?"
// A DNS name, optionally after *. for every name below it. Its last label is
// not a number: such a name is an IPv4 address mistyped
const HOSTNAME = new RegExp(
  `^(\\*\\.)?(${HOSTNAME_LABEL}\\.)*(?!\\d+$)${HOSTNAME_LABEL}$`
)
const MAX_HOSTNAME_LENGTH = 253

// What a typed destination names: an IP address or block as typed, or a host
// name in lowercase without its trailing dot. A block with host bits set
// gives the network it likely means instead
const readDestination = (
  value: string
):
  | Pick<AiPolicyNetworkDestination, "cidr">
  | Pick<AiPolicyNetworkDestination, "hostname">
  | { network: string }
  | undefined => {
  const cidr = parseCidr(value)

  if (cidr) return cidr.network ? { network: cidr.network } : { cidr: value }

  const hostname = value.toLowerCase().replace(/\.$/, "")

  if (hostname.length <= MAX_HOSTNAME_LENGTH && HOSTNAME.test(hostname))
    return { hostname }
}

// The address block or host name a destination row sends
const getDestinationTarget = (
  value = ""
): Pick<AiPolicyNetworkDestination, "cidr" | "hostname"> => {
  const target = readDestination(value.trim())

  return target && !("network" in target) ? target : {}
}

const SUBDOMAINS_PREFIX = "*."

// The host name a destination row names when it is a plain one: not an
// address and not a *. name
export const getPlainHostname = (value = "") => {
  const target = readDestination(value.trim())

  if (!target || !("hostname" in target) || !target.hostname) return undefined
  if (target.hostname.startsWith(SUBDOMAINS_PREFIX)) return undefined

  return target.hostname
}

// Whether a host name's *. name is not too long
const fitsSubdomains = (hostname: string) =>
  hostname.length + SUBDOMAINS_PREFIX.length <= MAX_HOSTNAME_LENGTH

// The host name a destination row names, when it can take its subdomains
// along: a plain host name whose *. name is not too long
export const getSubdomainsHostname = (value = "") => {
  const hostname = getPlainHostname(value)

  return hostname && fitsSubdomains(hostname) ? hostname : undefined
}

// Why a destination row's subdomains checkbox reads as it does: its tooltip
const getSubdomainsReason = (hostname?: string, fits?: boolean) => {
  if (!hostname) return "subdomainsHostnameOnly"
  if (!fits) return "errors.subdomainsTooLong"

  return "subdomainsTooltip"
}

// A destination row's subdomains checkbox. It always shows, so nothing comes
// or goes while the row is typed or the box is clicked. It can be checked only
// for a plain host name whose *. name is not too long, and unchecked always
export const getSubdomainsChoice = ({
  value,
  subdomains
}: AiPolicyFormDestination) => {
  const hostname = getPlainHostname(value)
  const fits = !!hostname && fitsSubdomains(hostname)

  return {
    hostname,
    reason: getSubdomainsReason(hostname, fits),
    disabled: !subdomains && !fits
  }
}

// Whether a destination row names something that never takes subdomains: an
// IP address, an address block or a *. name. Text that is not a destination
// yet, such as a host name being typed, can still become one that does
export const isNeverSubdomains = (value = "") => {
  const target = readDestination(value.trim())

  if (!target) return false

  return (
    !("hostname" in target) || !!target.hostname?.startsWith(SUBDOMAINS_PREFIX)
  )
}

// What is wrong with a destination row, if anything. A preset kept as loaded
// is sent untouched
export const getDestinationError = (
  t: TFunction,
  { value, ports, kept, subdomains }: AiPolicyFormDestination
) => {
  const tKey = "governance.ai.live.form.destinations.errors"
  const text = value?.trim()

  if (kept) return undefined
  if (!text) return t(`${tKey}.required`)

  const target = readDestination(text)

  if (!target) return t(`${tKey}.invalid`)
  if ("network" in target)
    return t(`${tKey}.hostBits`, { value: text, network: target.network })

  const hostname = subdomains ? getPlainHostname(text) : undefined

  if (hostname && !fitsSubdomains(hostname))
    return t(`${tKey}.subdomainsTooLong`, { hostname })
  if (!getPortRange(ports)) return t(`${tKey}.ports`)
}

// One port set alone is that port, not any port. Undefined for any port
export const getPortsText = ({
  portFrom,
  portTo
}: AiPolicyNetworkDestination) => {
  const from = portFrom || portTo
  const to = portTo || portFrom

  if (!from) return undefined

  return from === to ? `${from}` : `${from}-${to}`
}

// A preset can no longer be picked: one loaded is kept as it was
const toFormDestination = (
  destination: AiPolicyNetworkDestination
): AiPolicyFormDestination => {
  if (destination.preset) return { kept: destination }

  const value = destination.cidr ?? destination.hostname
  const ports = getPortsText(destination)

  return { ...(value && { value }), ...(ports && { ports }) }
}

// Pairs a host name with its *. name on the same ports: a host name and ports
// joined by a separator neither contains
const getPairKey = (hostname: string, ports = "") => `${hostname} ${ports}`

// A host name and its *. name on the same ports, as one row that takes its
// subdomains; the row stands where the first of the two did. Each row comes
// with the places in the list of the destinations it shows
const toIndexedFormDestinations = (
  destinations: AiPolicyNetworkDestination[]
): { row: AiPolicyFormDestination; indexes: number[] }[] => {
  const rows = destinations.map(toFormDestination)
  // The indexes of the *. rows, by the host name they are below and ports
  const subdomainRows = new Map<string, number[]>()

  rows.forEach(({ value, ports }, index) => {
    if (!value?.startsWith(SUBDOMAINS_PREFIX)) return

    const key = getPairKey(value.slice(SUBDOMAINS_PREFIX.length), ports)

    subdomainRows.set(key, [...(subdomainRows.get(key) ?? []), index])
  })

  // Each paired row's partner, both ways
  const pairs = new Map<number, number>()

  rows.forEach(({ value, ports }, index) => {
    if (!value || !getSubdomainsHostname(value)) return

    const subdomainIndex = subdomainRows.get(getPairKey(value, ports))?.shift()

    if (subdomainIndex === undefined) return

    pairs.set(index, subdomainIndex)
    pairs.set(subdomainIndex, index)
  })

  return rows.flatMap((row, index) => {
    const partner = pairs.get(index)

    if (partner === undefined) return [{ row, indexes: [index] }]
    if (partner < index) return []

    const hostnameRow = row.value?.startsWith(SUBDOMAINS_PREFIX)
      ? rows[partner]
      : row

    return [
      { row: { ...hostnameRow, subdomains: true }, indexes: [index, partner] }
    ]
  })
}

export const toFormDestinations = (
  destinations: AiPolicyNetworkDestination[]
): AiPolicyFormDestination[] =>
  toIndexedFormDestinations(destinations).map(({ row }) => row)

// What a row edits, to tell whether it is still as loaded
const getRowKey = ({ value, ports, subdomains }: AiPolicyFormDestination) =>
  JSON.stringify([value ?? "", ports ?? "", !!subdomains])

// The rows of a loaded condition, each keeping the destinations it was loaded
// from, so that a row left as loaded sends them as they were
export const toLoadedFormDestinations = (
  destinations: AiPolicyNetworkDestination[]
): AiPolicyFormDestination[] =>
  toIndexedFormDestinations(destinations).map(({ row, indexes }) => ({
    ...row,
    loaded: {
      row: getRowKey(row),
      destinations: indexes.map((index) => ({
        index,
        destination: destinations[index]
      }))
    }
  }))

// A row that takes its subdomains sends its host name and its *. name
const toApiDestinations = ({
  value,
  ports,
  kept,
  subdomains
}: AiPolicyFormDestination): AiPolicyNetworkDestination[] => {
  if (kept) return [kept]

  const portRange = getPortRange(ports)
  const hostname = subdomains ? getSubdomainsHostname(value) : undefined

  if (hostname)
    return [
      { hostname, ...portRange },
      { hostname: `${SUBDOMAINS_PREFIX}${hostname}`, ...portRange }
    ]

  return [{ ...getDestinationTarget(value), ...portRange }]
}

// A condition's access types and destinations as rules of its card, each on
// its own
const getAccessRules = ({
  accessTypes = [],
  networkDestinations = []
}: AiPolicyCondition): AiPolicyFormRule[] => [
  ...(accessTypes.length
    ? [{ condition: ACCESS_TYPES_RULE, value: accessTypes }]
    : []),
  ...(networkDestinations.length
    ? [
        {
          condition: NETWORK_DESTINATION_RULE,
          destinations: toLoadedFormDestinations(networkDestinations)
        }
      ]
    : [])
]

// The access types of a card's rules, in their usual order
export const getAccessTypes = (
  rules: (AiPolicyFormRule | undefined)[]
): AiPolicyAccessType[] => {
  const selected = rules
    .filter((rule) => rule?.condition === ACCESS_TYPES_RULE)
    .flatMap((rule) => [rule?.value ?? []].flat())

  return AI_POLICY_ACCESS_TYPES.filter((type) => selected.includes(type))
}

// A destination's fields in a fixed order, whatever order its keys come in
const getDestinationKey = ({
  cidr,
  hostname,
  preset,
  portFrom,
  portTo
}: AiPolicyNetworkDestination) =>
  JSON.stringify([cidr, hostname, preset, portFrom, portTo])

// The destinations a row was loaded from, while it is as loaded
const getLoadedDestinations = (row: AiPolicyFormDestination) =>
  row.loaded?.row === getRowKey(row) ? row.loaded.destinations : undefined

// What a condition's destination rows send. A row left as loaded sends the
// destinations it was loaded from, as they were, repeats included; with no
// row new or edited, that is the loaded list in its order, less the rows
// removed. A new or edited row sends each destination once: not again when
// another row already sends it, as a host name row with its subdomains and a
// *. row of the same name do. A row left as loaded counts as sending both
// what it was loaded from and what it shows, so X.com. or one port bound
// loaded is not sent again as x.com or as that port from and to
const toApiDestinationList = (rows: AiPolicyFormDestination[]) => {
  const loaded = rows.map(getLoadedDestinations)

  if (loaded.every(Boolean))
    return loaded
      .flatMap((destinations) => destinations ?? [])
      .sort((a, b) => a.index - b.index)
      .map(({ destination }) => destination)

  const seen = new Set(
    rows.flatMap((row, index) => {
      const destinations = loaded[index]

      if (!destinations) return []

      return [
        ...destinations.map(({ destination }) => destination),
        ...toApiDestinations(row)
      ].map(getDestinationKey)
    })
  )

  return rows.flatMap((row, index) => {
    const destinations = loaded[index]

    if (destinations) return destinations.map(({ destination }) => destination)

    return toApiDestinations(row).filter((destination) => {
      const key = getDestinationKey(destination)

      if (seen.has(key)) return false

      seen.add(key)

      return true
    })
  })
}

// The access types and destinations a card's rules set, as its condition's
const getAccess = (
  rules: AiPolicyFormRule[]
): Pick<AiPolicyCondition, "accessTypes" | "networkDestinations"> => {
  const destinations = rules.filter(
    (rule) => rule.condition === NETWORK_DESTINATION_RULE
  )

  return {
    ...(rules.some((rule) => rule.condition === ACCESS_TYPES_RULE) && {
      accessTypes: getAccessTypes(rules)
    }),
    ...(destinations.length > 0 && {
      networkDestinations: toApiDestinationList(
        destinations.flatMap((rule) => rule.destinations ?? [])
      )
    })
  }
}

export const getDecisionOutcome = (
  decision: Pick<AiPolicyDecision, "blocked" | "violation">
): AiDecisionOutcome => {
  if (decision.blocked) return "blocked"
  if (decision.violation) return "wouldBlock"

  return "allowed"
}

// The policies to ask the decisions of, as the API's comma-separated ids: the
// ones picked, or else every policy, as the API also serves the calls no
// policy decided on, with no policy entry. Policy kinds narrow both: a picked
// policy of another kind is not asked about. Sorted, so reordering the
// policies asks the same; undefined while there is no policy to ask about, as
// when no picked policy is of the kinds
export const getDecisionsPolicyId = (
  policyId: string | undefined,
  policies: Pick<AiPolicy, "id" | "kind">[],
  kinds: AiPolicyKind[] = []
) => {
  const ofKinds = kinds.length
    ? policies.filter((policy) => kinds.includes(getAiPolicyKind(policy)))
    : policies

  if (policyId && !kinds.length) return policyId
  if (policyId) {
    const ids = new Set(ofKinds.map(({ id }) => id))

    return (
      policyId
        .split(",")
        .filter((id) => ids.has(id))
        .join(",") || undefined
    )
  }

  return (
    ofKinds
      .map(({ id }) => id)
      .sort()
      .join(",") || undefined
  )
}

export const getAiPolicyDecisionRows = (
  decisions: AiPolicyDecision[],
  policies: AiPolicy[]
) => {
  const policyNames = new Map(policies.map(({ id, name }) => [id, name]))

  return decisions.map((decision) => ({
    ...decision,
    key: decision.id,
    decided: getDateLabels(decision.decidedAt),
    platformLabel: getPlatformLabel(decision.platform),
    // An MCP server policy's decision names the server, a workload's the
    // workload, not an agent
    agent:
      decision.subject === "workload"
        ? decision.workload || ""
        : decision.mcpServerName || decision.agentName || decision.agentId,
    policyName: decision.policyId
      ? policyNames.get(decision.policyId) || decision.policyId
      : NO_VALUE_PLACEHOLDER,
    outcome: getDecisionOutcome(decision),
    enforcedBy: decision.enforcedBy || NO_VALUE_PLACEHOLDER,
    reason: decision.reason || "",
    workloadLabel: getWorkloadLabel(decision)
  }))
}

// The Kubernetes workload a call was caught in, as namespace/name; undefined
// off Kubernetes
const getWorkloadLabel = ({
  namespace,
  workload
}: Pick<AiPolicyDecision, "namespace" | "workload">) =>
  workload ? [namespace, workload].filter(Boolean).join("/") : undefined
